import React, { memo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useTimer } from '../../hooks/useTimer';

dayjs.extend(utc);
dayjs.extend(timezone);

const timezoneFormatters = new Map();

const isValidTimezone = timeZone => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });

    return true;
  } catch {
    return false;
  }
};

const getTimezoneFormatter = timeZone => {
  if (!timezoneFormatters.has(timeZone)) {
    timezoneFormatters.set(
      timeZone,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
      }),
    );
  }

  return timezoneFormatters.get(timeZone);
};

const getTimezoneAbbreviation = (timeZone, date) =>
  getTimezoneFormatter(timeZone)
    .formatToParts(date)
    .find(part => part.type === 'timeZoneName')?.value;

const LocalTimeZoneComponent = ({ timezone, format, defaultValue = '', showTimezoneAbbreviation = false }) => {
  if (!timezone) {
    return <span>N/A</span>;
  }

  const now = useTimer();

  if (!isValidTimezone(timezone)) {
    return <span>{defaultValue}</span>;
  }

  const formatted = dayjs(now)
    .tz(timezone)
    .format(format);
  const timezoneAbbreviation = getTimezoneAbbreviation(timezone, now);

  const value = showTimezoneAbbreviation && timezoneAbbreviation ? `${formatted} ${timezoneAbbreviation}` : formatted;

  return <span>{value}</span>;
};

export const LocalTimeZone = memo(LocalTimeZoneComponent);
